import React, { useState, useEffect } from 'react';
import { Card, Switch, Spin, message, Alert, Typography, Divider, Tag, Tooltip, Radio } from 'antd';
import {
  BellOutlined,
  MessageOutlined,
  ShoppingCartOutlined,
  CloseCircleOutlined,
  CarOutlined,
  SmileOutlined,
  CameraOutlined,
  GiftOutlined,
  InfoCircleOutlined,
  ClockCircleOutlined,
  CalendarOutlined,
  WarningOutlined,
  UserAddOutlined,
  CreditCardOutlined
} from '@ant-design/icons';
import { userSettingsApi } from '../../utils/api';

const { Title, Text, Paragraph } = Typography;

/**
 * Composant pour gérer les préférences d'envoi automatique de messages WhatsApp
 * Permet d'activer/désactiver globalement et par événement
 */
const WhatsAppAutoMessagesSettings = () => {
  // État des préférences
  const [preferences, setPreferences] = useState({
    allow_new_contacts: false,
    whatsapp_auto_messages: {
      global_enabled: true,
      events: {},
      admin_notification_channels: {}
    }
  });

  // État des descriptions des événements (récupérées du serveur)
  const [eventDescriptions, setEventDescriptions] = useState({});

  // États de chargement et sauvegarde
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Liste des événements dépréciés à filtrer de l'affichage
  const deprecatedEvents = ['MSGSATISFACTION', 'MSGPHOTO', 'MSGPROMO'];

  // Événements masqués de l'interface (toujours gérés côté serveur, valeur inchangée)
  const hiddenEvents = ['DIGITAL_PRODUCT'];

  // Événements pour lesquels l'administrateur peut choisir le canal de réception.
  const adminChannelEvents = ['ADMIN_NEW_ORDER', 'ADMIN_ORDER_CANCELLED', 'ADMIN_ORDER_DEPOSIT_CONFIRMED'];

  // Icône correspondant à chaque type d'événement
  const eventIcons = {
    ADMIN_NEW_ORDER: <ShoppingCartOutlined />,
    ADMIN_ORDER_CANCELLED: <CloseCircleOutlined />,
    ADMIN_ORDER_DEPOSIT_CONFIRMED: <CreditCardOutlined />,
    MSGLIVREUR: <CarOutlined />,
    MSGSATISFACTION: <SmileOutlined />,
    MSGPHOTO: <CameraOutlined />,
    MSGPROMO: <GiftOutlined />,
    MSG2JOURS: <ClockCircleOutlined />,    // Icône pour message J+2
    MSG7JOURS: <CalendarOutlined />        // Icône pour message J+7
  };

  // Couleurs des tags pour les déclencheurs
  const triggerColors = {
    'Création de commande': 'green',
    'Annulation de commande': 'red',
    // Heures d'envoi planifiées (libellés renvoyés par le serveur)
    '09h00': 'blue',
    '19h00': 'orange',
    '19h30': 'orange',
    '20h00': 'purple',
    'Paiement réussi': 'cyan'
  };

  // Chargement des préférences au montage du composant
  useEffect(() => {
    loadPreferences();
  }, []);

  /**
   * Charge les préférences depuis le serveur
   */
  const loadPreferences = async () => {
    try {
      setLoading(true);
      const response = await userSettingsApi.getNotificationPreferences();

      if (response.data?.success) {
        setPreferences(response.data.data.preferences);
        setEventDescriptions(response.data.data.eventDescriptions);
      } else {
        message.error('Erreur lors du chargement des préférences');
      }
    } catch (error) {
      console.error('Erreur chargement préférences:', error);
      message.error('Erreur lors du chargement des préférences');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Met à jour les préférences sur le serveur
   * @param {Object} newPreferences - Nouvelles préférences à sauvegarder
   */
  const updatePreferences = async (newPreferences) => {
    try {
      setSaving(true);
      const response = await userSettingsApi.updateNotificationPreferences(newPreferences);

      if (response.data?.success) {
        setPreferences(newPreferences);
        message.success('Préférences mises à jour');
      } else {
        message.error('Erreur lors de la mise à jour');
        // Recharger les préférences en cas d'erreur
        loadPreferences();
      }
    } catch (error) {
      console.error('Erreur mise à jour préférences:', error);
      message.error('Erreur lors de la mise à jour des préférences');
      loadPreferences();
    } finally {
      setSaving(false);
    }
  };

  /**
   * Gère le changement du switch global
   * @param {boolean} checked - Nouvel état du switch
   */
  const handleGlobalToggle = (checked) => {
    const newPreferences = {
      ...preferences,
      whatsapp_auto_messages: {
        ...preferences.whatsapp_auto_messages,
        global_enabled: checked
      }
    };
    updatePreferences(newPreferences);
  };

  /**
   * Gère le changement du switch allow_new_contacts
   * @param {boolean} checked - Nouvel état du switch
   */
  const handleAllowNewContactsToggle = (checked) => {
    const newPreferences = {
      ...preferences,
      allow_new_contacts: checked
    };
    updatePreferences(newPreferences);
  };

  /**
   * Gère le changement d'un switch individuel d'événement
   * @param {string} eventKey - Clé de l'événement
   * @param {boolean} checked - Nouvel état du switch
   */
  const handleEventToggle = (eventKey, checked) => {
    const newPreferences = {
      ...preferences,
      whatsapp_auto_messages: {
        ...preferences.whatsapp_auto_messages,
        events: {
          ...preferences.whatsapp_auto_messages.events,
          [eventKey]: checked
        }
      }
    };
    updatePreferences(newPreferences);
  };

  /**
   * Enregistre le canal de réception d'une notification administrateur.
   * Chaque événement concerné peut être envoyé sur Telegram, WhatsApp ou les deux,
   * tandis que les autres messages continuent d'utiliser leur réglage existant.
   * @param {string} eventKey - Clé de l'événement administrateur à modifier
   * @param {'telegram'|'whatsapp'|'both'} channel - Canal choisi dans l'interface
   */
  const handleAdminChannelChange = (eventKey, channel) => {
    const newPreferences = {
      ...preferences,
      whatsapp_auto_messages: {
        ...preferences.whatsapp_auto_messages,
        admin_notification_channels: {
          ...preferences.whatsapp_auto_messages.admin_notification_channels,
          [eventKey]: channel
        }
      }
    };
    updatePreferences(newPreferences);
  };

  // Affichage du loader pendant le chargement
  if (loading) {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <Spin size="large" />
          <Text style={{ display: 'block', marginTop: 16 }}>Chargement des préférences...</Text>
        </div>
      </Card>
    );
  }

  const globalEnabled = preferences.whatsapp_auto_messages?.global_enabled ?? true;
  const events = preferences.whatsapp_auto_messages?.events || {};
  const adminNotificationChannels = preferences.whatsapp_auto_messages?.admin_notification_channels || {};

  return (
    <div className="space-y-6">
      {/* Carte principale avec switch global */}
      <Card
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <BellOutlined style={{ fontSize: 20 }} />
            <span>Notifications automatiques</span>
          </div>
        }
        extra={
          <Tooltip title={globalEnabled ? 'Désactiver tous les messages' : 'Activer tous les messages'}>
            <Switch
              checked={globalEnabled}
              onChange={handleGlobalToggle}
              loading={saving}
              checkedChildren="ON"
              unCheckedChildren="OFF"
            />
          </Tooltip>
        }
      >
        {/* Message d'information */}
        <Alert
          message="Contrôle des messages automatiques"
          description={
            <span>
              Activez ou désactivez l'envoi automatique de notifications pour chaque type d'événement.
              <br />
              <strong>Note :</strong> Les messages désactivés ne seront pas envoyés et ne seront pas comptabilisés dans votre quota.
            </span>
          }
          type="info"
          showIcon
          icon={<InfoCircleOutlined />}
          style={{ marginBottom: 24 }}
        />

        {/* Indicateur de l'état global */}
        {!globalEnabled && (
          <Alert
            message="Messages automatiques désactivés"
            description="Tous les messages automatiques sont actuellement désactivés. Activez le switch principal pour reprendre les envois."
            type="warning"
            showIcon
            style={{ marginBottom: 24 }}
          />
        )}

        {/* Section : Autoriser l'envoi aux nouveaux contacts */}
        <Card
          size="small"
          style={{
            marginBottom: 24,
            border: preferences.allow_new_contacts ? '1px solid #faad14' : '1px solid #d9d9d9'
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                <UserAddOutlined style={{ fontSize: 18 }} />
                <Title level={5} style={{ margin: 0 }}>Envoyer aux nouveaux contacts</Title>
                <Tag color="orange">Risque</Tag>
              </div>
              <Paragraph type="secondary" style={{ margin: 0, fontSize: 13 }}>
                Si activé, les messages seront envoyés même aux contacts avec lesquels vous n'avez jamais échangé.
              </Paragraph>
              {/* Avertissement visible si activé */}
              {preferences.allow_new_contacts && (
                <Alert
                  message="Attention : risque de blocage WhatsApp"
                  description="Envoyer des messages à des contacts inconnus augmente significativement le risque que WhatsApp bloque votre numéro. Utilisez cette option avec précaution."
                  type="warning"
                  showIcon
                  icon={<WarningOutlined />}
                  style={{ marginTop: 8 }}
                />
              )}
            </div>
            <div style={{ marginLeft: 16 }}>
              <Tooltip title={preferences.allow_new_contacts ? 'Désactiver (recommandé)' : 'Activer (risque de blocage)'}>
                <Switch
                  checked={preferences.allow_new_contacts}
                  onChange={handleAllowNewContactsToggle}
                  loading={saving}
                  size="small"
                />
              </Tooltip>
            </div>
          </div>
        </Card>

        <Divider orientation="left">
          <MessageOutlined /> Configuration par événement
        </Divider>

        {/* Liste des événements avec switches individuels */}
        {/* Filtrage des événements dépréciés (MSGSATISFACTION, MSGPHOTO, MSGPROMO) et masqués (DIGITAL_PRODUCT) */}
        <div className="space-y-4">
          {Object.keys(events).filter(key => !deprecatedEvents.includes(key) && !hiddenEvents.includes(key)).map((eventKey) => {
            const eventDesc = eventDescriptions[eventKey] || {
              label: eventKey,
              description: 'Description non disponible',
              trigger: 'N/A'
            };
            const isEnabled = events[eventKey];
            const isDisabledByGlobal = !globalEnabled;

            return (
              <Card
                key={eventKey}
                size="small"
                style={{
                  opacity: isDisabledByGlobal ? 0.6 : 1,
                  transition: 'opacity 0.3s'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                      <span style={{ fontSize: 18 }}>{eventIcons[eventKey]}</span>
                      <Title level={5} style={{ margin: 0 }}>{eventDesc.label}</Title>
                      <Tag color={triggerColors[eventDesc.trigger] || 'default'}>
                        {eventDesc.trigger}
                      </Tag>
                    </div>
                    <Paragraph type="secondary" style={{ margin: 0, fontSize: 13 }}>
                      {eventDesc.description}
                    </Paragraph>
                    {adminChannelEvents.includes(eventKey) && (
                      <div style={{ marginTop: 12 }}>
                        <Text strong style={{ fontSize: 13 }}>Canal de réception admin :</Text>
                        <Radio.Group
                          value={adminNotificationChannels[eventKey] || 'telegram'}
                          onChange={(event) => handleAdminChannelChange(eventKey, event.target.value)}
                          disabled={isDisabledByGlobal || saving || !isEnabled}
                          size="small"
                          style={{ display: 'block', marginTop: 6 }}
                        >
                          <Radio.Button value="telegram">Telegram</Radio.Button>
                          <Radio.Button value="whatsapp">WhatsApp</Radio.Button>
                          <Radio.Button value="both">Les deux</Radio.Button>
                        </Radio.Group>
                      </div>
                    )}
                  </div>
                  <div style={{ marginLeft: 16 }}>
                    <Tooltip
                      title={
                        isDisabledByGlobal
                          ? 'Activez d\'abord le switch global'
                          : isEnabled
                            ? 'Désactiver cet événement'
                            : 'Activer cet événement'
                      }
                    >
                      <Switch
                        checked={isEnabled && globalEnabled}
                        onChange={(checked) => handleEventToggle(eventKey, checked)}
                        disabled={isDisabledByGlobal || saving}
                        loading={saving}
                        size="small"
                      />
                    </Tooltip>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      </Card>
    </div>
  );
};

export default WhatsAppAutoMessagesSettings;
